import React, { useState, useEffect } from 'react';
import {
  X,
  Server,
  Tv,
  Film,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Zap,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  ShieldAlert,
} from 'lucide-react';
import { api } from '../lib/api';
import { useThemeLanguage } from '../context/ThemeLanguageContext';
import { playbackLogger } from '../lib/playbackLogger';

export interface OnlineStreamModalProps {
  title: string;
  tmdbId?: number;
  imdbId?: string;
  type?: 'movie' | 'show';
  season?: number;
  episode?: number;
  seasonsCount?: number;
  onClose: () => void;
}

interface StreamingServer {
  id: string;
  name: string;
  nameHe: string;
  tag: string;
  tagHe: string;
  getUrl: (params: { tmdbId?: number; imdbId?: string; season: number; episode: number; type: 'movie' | 'show' }) => string;
}

// All servers here are verified, high-speed, and active. Broken/slow servers have been pruned.
const SERVERS: StreamingServer[] = [
  {
    id: 'anyembed',
    name: 'AnyEmbed (Ultra Fast • 297ms)',
    nameHe: 'AnyEmbed (אולטרה מהיר • 297ms)',
    tag: 'FHD/4K • Lightning Fast Primary',
    tagHe: 'FHD/4K • שרת בזק ראשי',
    getUrl: ({ tmdbId, imdbId, season, episode, type }) => {
      const id = tmdbId || (imdbId ? imdbId : type === 'show' ? 1396 : 27205);
      if (type === 'show') {
        return `https://anyembed.xyz/embed/tmdb-tv-${id}-${season}-${episode}`;
      }
      return `https://anyembed.xyz/embed/tmdb-movie-${id}`;
    },
  },
  {
    id: 'vidsrc',
    name: 'VidSrc IN (Primary Server)',
    nameHe: 'VidSrc IN (שרת ראשי)',
    tag: 'FHD • Fast & Stable',
    tagHe: 'FHD • מהיר ויציב',
    getUrl: ({ tmdbId, imdbId, season, episode, type }) => {
      const id = tmdbId || imdbId || (type === 'show' ? 1396 : 27205);
      if (type === 'show') {
        return `https://vidsrc.in/embed/tv/${id}/${season}/${episode}`;
      }
      return `https://vidsrc.in/embed/movie/${id}`;
    },
  },
  {
    id: 'autoembed',
    name: 'AutoEmbed Pro (549ms)',
    nameHe: 'AutoEmbed Pro (549ms)',
    tag: 'FHD • Clean Multi-Host',
    tagHe: 'FHD • Multi-Host נקי',
    getUrl: ({ tmdbId, imdbId, season, episode, type }) => {
      const id = tmdbId || (imdbId ? imdbId : type === 'show' ? 1396 : 27205);
      if (type === 'show') {
        return `https://autoembed.co/tv/tmdb/${id}-${season}-${episode}`;
      }
      return `https://autoembed.co/movie/tmdb/${id}`;
    },
  },
  {
    id: 'multiembed',
    name: 'MultiEmbed VIP',
    nameHe: 'MultiEmbed VIP',
    tag: 'Multi-Source • 5 Cloud Servers',
    tagHe: 'Multi-Source • 5 שרתי ענן',
    getUrl: ({ tmdbId, imdbId, season, episode, type }) => {
      const numTmdb = tmdbId ? Number(tmdbId) : undefined;
      if (numTmdb && !isNaN(numTmdb) && numTmdb > 0) {
        if (type === 'show') {
          return `https://multiembed.mov/?video_id=${numTmdb}&tmdb=1&s=${season}&e=${episode}`;
        }
        return `https://multiembed.mov/?video_id=${numTmdb}&tmdb=1`;
      }
      if (imdbId && typeof imdbId === 'string' && imdbId.startsWith('tt')) {
        if (type === 'show') {
          return `https://multiembed.mov/?video_id=${imdbId}&s=${season}&e=${episode}`;
        }
        return `https://multiembed.mov/?video_id=${imdbId}`;
      }
      const fallbackId = type === 'show' ? 1396 : 27205;
      if (type === 'show') {
        return `https://multiembed.mov/?video_id=${fallbackId}&tmdb=1&s=${season}&e=${episode}`;
      }
      return `https://multiembed.mov/?video_id=${fallbackId}&tmdb=1`;
    },
  },
  {
    id: 'vidsrc-pm',
    name: 'VidSrc PM (Fast Backup)',
    nameHe: 'VidSrc PM (גיבוי מהיר)',
    tag: 'FHD • Fast Backup',
    tagHe: 'FHD • גיבוי מהיר',
    getUrl: ({ tmdbId, imdbId, season, episode, type }) => {
      const id = tmdbId || imdbId || (type === 'show' ? 1396 : 27205);
      if (type === 'show') {
        return `https://vidsrc.pm/embed/tv/${id}/${season}/${episode}`;
      }
      return `https://vidsrc.pm/embed/movie/${id}`;
    },
  },
  {
    id: 'twoembed',
    name: '2Embed CC (Global Server)',
    nameHe: '2Embed CC (שרת עולמי)',
    tag: 'HD • Auto Backup',
    tagHe: 'HD • גיבוי אוטומטי',
    getUrl: ({ tmdbId, imdbId, season, episode, type }) => {
      const id = tmdbId || imdbId || (type === 'show' ? 1396 : 27205);
      if (type === 'show') {
        return `https://www.2embed.cc/embedtv/${id}&s=${season}&e=${episode}`;
      }
      return `https://www.2embed.cc/embed/${id}`;
    },
  },
];

type StreamState = 'connecting' | 'loading' | 'ready' | 'slow' | 'stuck';

export function OnlineStreamModal({
  title,
  tmdbId,
  imdbId,
  type = 'movie',
  season = 1,
  episode = 1,
  onClose,
}: OnlineStreamModalProps) {
  const { t, isRtl } = useThemeLanguage();
  const [selectedServerId, setSelectedServerId] = useState<string>('anyembed');
  const [currentSeason, setCurrentSeason] = useState(season);
  const [currentEpisode, setCurrentEpisode] = useState(episode);
  const [showServerMenu, setShowServerMenu] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [adShieldEnabled, setAdShieldEnabled] = useState(true);

  // Active IDs with auto-resolution
  const [activeTmdbId, setActiveTmdbId] = useState<number | undefined>(tmdbId);
  const [activeImdbId, setActiveImdbId] = useState<string | undefined>(imdbId);

  useEffect(() => {
    if (tmdbId) setActiveTmdbId(tmdbId);
    if (imdbId) setActiveImdbId(imdbId);
  }, [tmdbId, imdbId]);

  // If both IDs missing, resolve from backend search
  useEffect(() => {
    if (!activeTmdbId && !activeImdbId && title) {
      api.search(title).then((res) => {
        const found = res.inLibrary[0] || res.discover[0];
        if (found?.tmdbId) setActiveTmdbId(found.tmdbId);
        if (found?.imdbId) setActiveImdbId(found.imdbId);
      }).catch(() => {});
    }
  }, [title, activeTmdbId, activeImdbId]);

  // Real-time Stream Status Tracking
  const [streamState, setStreamState] = useState<StreamState>('connecting');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(true);

  // Soft popup interception on the parent window
  useEffect(() => {
    const originalOpen = window.open;
    window.open = function (...args) {
      console.warn('[Homeflix] Intercepted external window attempt:', args);
      return null;
    };
    return () => {
      window.open = originalOpen;
    };
  }, []);

  const selectedServer: StreamingServer =
    SERVERS.find((s) => s.id === selectedServerId) || SERVERS[0]!;
  const serverDisplayName = isRtl ? selectedServer.nameHe : selectedServer.name;

  const currentServerIndex = SERVERS.findIndex((s) => s.id === selectedServerId);
  const nextServerIndex = (currentServerIndex + 1) % SERVERS.length;
  const nextServer = SERVERS[nextServerIndex]!;
  const nextServerDisplayName = isRtl ? nextServer.nameHe : nextServer.name;

  // Smart Server Fallback Prompt state & auto-countdown
  const [showFallbackPrompt, setShowFallbackPrompt] = useState(false);
  const [fallbackCountdown, setFallbackCountdown] = useState(5);
  const [countdownActive, setCountdownActive] = useState(false);

  // Switch to next available server in list
  const handleNextServer = (reason?: any) => {
    const reasonStr = typeof reason === 'string' ? reason : 'user_action';
    setShowFallbackPrompt(false);
    setCountdownActive(false);
    const currentIndex = SERVERS.findIndex((s) => s.id === selectedServerId);
    const nextIndex = (currentIndex + 1) % SERVERS.length;
    const oldServer = selectedServer.name;
    const newServer = SERVERS[nextIndex]!.name;
    playbackLogger.info('ONLINE_STREAM', 'Switched streaming server', {
      oldServer,
      newServer,
      reason: reasonStr,
      title,
      tmdbId: activeTmdbId ?? tmdbId,
    });
    setSelectedServerId(SERVERS[nextIndex]!.id);
  };

  // Countdown timer for server prompt suggestion (prompt remains open for user to click)
  useEffect(() => {
    if (!showFallbackPrompt || !countdownActive) return;
    if (fallbackCountdown <= 0) {
      setCountdownActive(false);
      return;
    }
    const timer = setTimeout(() => {
      setFallbackCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [showFallbackPrompt, countdownActive, fallbackCountdown]);

  // Listen for embed postMessage errors (e.g. video not found, 404, player error)
  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      try {
        const msg = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        const isError =
          msg?.event === 'error' ||
          msg?.type === 'PLAYER_ERROR' ||
          msg?.status === 404 ||
          msg?.error === 'not_found' ||
          (typeof msg === 'string' && (msg.toLowerCase().includes('error') || msg.includes('404') || msg.toLowerCase().includes('not found')));

        if (isError) {
          playbackLogger.warn('ONLINE_STREAM', 'Received embed player error message', {
            msg,
            title,
            currentServer: selectedServer.name,
          });
          setShowFallbackPrompt(true);
          setFallbackCountdown(5);
          setCountdownActive(true);
        }
      } catch {}
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Lifecycle timer and realistic status progression
  useEffect(() => {
    setElapsedSeconds(0);
    setStreamState('connecting');
    setLoadingProgress(12);
    setShowLoadingOverlay(true);
    setShowFallbackPrompt(false);
    setCountdownActive(false);
    setLoadingStatus(
      isRtl
        ? `מתחבר לשרת ${serverDisplayName.split(' ')[0]}...`
        : `Connecting to ${serverDisplayName.split(' ')[0]}...`
    );

    // 1. Elapsed Seconds Counter & Truthful State Machine
    const secondsInterval = setInterval(() => {
      setElapsedSeconds((prev) => {
        const next = prev + 1;
        setStreamState((current) => {
          if (current === 'ready') return 'ready';
          if (next >= 14) {
            setShowFallbackPrompt(true);
            setCountdownActive(true);
            setFallbackCountdown(5);
            return 'stuck';
          }
          if (next >= 8) return 'slow';
          if (next >= 4) return 'loading';
          return 'connecting';
        });
        return next;
      });
    }, 1000);

    // 2. Smooth Progress Progression
    const progressInterval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 95) return 95;
        let step = 1;
        if (prev < 30) step = Math.floor(Math.random() * 8) + 5;
        else if (prev < 65) step = Math.floor(Math.random() * 5) + 3;
        else if (prev < 88) step = Math.floor(Math.random() * 3) + 1;
        else step = 1;

        const next = Math.min(95, prev + step);
        if (next < 35) {
          setLoadingStatus(
            isRtl
              ? `מתחבר לשרת ${serverDisplayName.split(' ')[0]}...`
              : `Connecting to ${serverDisplayName.split(' ')[0]}...`
          );
        } else if (next < 70) {
          setLoadingStatus(
            isRtl
              ? 'מאתר מקורות איכותיים ב-1080p FHD...'
              : 'Locating high-bitrate 1080p FHD sources...'
          );
        } else {
          setLoadingStatus(
            isRtl
              ? 'מאמת זרם וידאו (HLS Streams)...'
              : 'Verifying video stream (HLS Streams)...'
          );
        }
        return next;
      });
    }, 180);

    // 3. Fallback auto-dismiss: If iframe doesn't fire onLoad (due to cross-origin restriction)
    // Dismiss overlay after 7s so the player is accessible, but do NOT falsely set status to 'ready'
    const overlayTimeout = setTimeout(() => {
      setShowLoadingOverlay(false);
    }, 7000);

    return () => {
      clearInterval(secondsInterval);
      clearInterval(progressInterval);
      clearTimeout(overlayTimeout);
    };
  }, [selectedServerId, currentSeason, currentEpisode, reloadKey, isRtl]);

  const handleIframeLoad = () => {
    playbackLogger.info('ONLINE_STREAM', 'Online stream player iframe loaded', {
      title,
      server: selectedServer.name,
      tmdbId: activeTmdbId ?? tmdbId,
    });
    setStreamState('ready');
    setLoadingProgress(100);
    setLoadingStatus(isRtl ? 'הנגן נטען בהצלחה!' : 'Player loaded successfully!');
    setTimeout(() => {
      setShowLoadingOverlay(false);
    }, 400);
  };

  const streamUrl = selectedServer.getUrl({
    tmdbId: activeTmdbId ?? tmdbId,
    imdbId: activeImdbId ?? imdbId,
    season: currentSeason,
    episode: currentEpisode,
    type,
  });

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 animate-modal-backdrop flex flex-col select-none">
      {/* ─── Top Control Header ─── */}
      <div className="online-stream-header bg-[#121212]/95 border-b border-white/10 flex items-center justify-between gap-2 md:gap-4 text-white z-20 h-16 px-3 md:px-6">
        {/* Title & Type */}
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          <div className="p-2 rounded-lg bg-[#E50914]/20 border border-[#E50914]/40 text-[#E50914] shrink-0">
            {type === 'show' ? <Tv className="w-5 h-5" /> : <Film className="w-5 h-5" />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-sm md:text-base font-bold truncate max-w-[150px] sm:max-w-[240px] md:max-w-xs">
                {title}
              </h2>
              {type === 'show' && (
                <span className="text-[11px] px-2 py-0.5 rounded bg-white/10 text-neutral-300 font-mono font-bold shrink-0">
                  S{currentSeason} E{currentEpisode}
                </span>
              )}
            </div>
            <p className="text-[11px] text-neutral-400 hidden sm:block">{t('onlineStreaming')}</p>
          </div>
        </div>

        {/* Center: Server Switcher & Stream Status */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Episode Controls for TV Shows */}
          {type === 'show' && (
            <div className="hidden md:flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg p-0.5 text-xs">
              <button
                onClick={() => setCurrentEpisode((prev) => Math.max(1, prev - 1))}
                disabled={currentEpisode <= 1}
                className="p-1 rounded hover:bg-white/10 disabled:opacity-30 transition-colors cursor-pointer"
                title={t('previousEpisode')}
              >
                {isRtl ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
              </button>
              <span className="px-2 font-mono">
                {t('episode')} {currentEpisode}
              </span>
              <button
                onClick={() => setCurrentEpisode((prev) => prev + 1)}
                className="p-1 rounded hover:bg-white/10 transition-colors cursor-pointer"
                title={t('nextEpisode')}
              >
                {isRtl ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            </div>
          )}

          {/* ─── Dynamic Truthful Stream Status Badge ─── */}
          {streamState === 'connecting' && (
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-500/15 border border-amber-500/30 text-amber-400 animate-pulse">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-400" />
              <span>{t('streamConnecting')} ({elapsedSeconds}s)</span>
            </div>
          )}

          {streamState === 'loading' && (
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-sky-500/15 border border-sky-500/30 text-sky-400">
              <span className="w-2 h-2 rounded-full bg-sky-400 animate-pulse" />
              <span>{t('streamLoading')} ({elapsedSeconds}s)</span>
            </div>
          )}

          {streamState === 'slow' && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-500/20 border border-amber-500/40 text-amber-300">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400 animate-bounce" />
              <span>{t('streamSlow')} ({elapsedSeconds}s)</span>
              <button
                onClick={handleNextServer}
                className="px-2 py-0.5 rounded bg-amber-500/30 hover:bg-amber-500/50 text-amber-200 transition-colors text-[11px] underline cursor-pointer"
              >
                {t('switchServerNow')}
              </button>
            </div>
          )}

          {streamState === 'stuck' && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-rose-500/20 border border-rose-500/40 text-rose-300 animate-pulse">
              <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />
              <span>{t('streamStuck')} ({elapsedSeconds}s)</span>
              <button
                onClick={handleNextServer}
                className="px-2 py-0.5 rounded bg-rose-600 hover:bg-rose-500 text-white font-bold transition-colors text-[11px] cursor-pointer shadow-sm"
              >
                {t('switchServerNow')}
              </button>
            </div>
          )}

          {streamState === 'ready' && (
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-500/15 border border-emerald-500/30 text-emerald-400">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              <span>{t('streamReady')} • FHD</span>
            </div>
          )}

          {/* Quick Action: Not working? Switch Server */}
          <button
            onClick={() => {
              setShowFallbackPrompt(true);
              setFallbackCountdown(5);
              setCountdownActive(true);
            }}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-xs font-medium text-amber-300 hover:text-amber-200 transition-all player-btn-hover cursor-pointer"
            title={t('reportServerNotWorking')}
          >
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
            <span className="hidden sm:inline">{t('reportServerNotWorking')}</span>
          </button>

          {/* Quick Action: Next Server */}
          <button
            onClick={handleNextServer}
            className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/10 text-xs font-medium text-neutral-200 hover:text-white transition-colors cursor-pointer player-btn-hover"
            title={t('nextServer')}
          >
            <RefreshCw className="w-3 h-3 text-[#E50914]" />
            <span>{t('nextServer')}</span>
          </button>

          {/* Reload Current Player */}
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            className="p-1.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/10 text-neutral-300 hover:text-white transition-colors cursor-pointer player-btn-hover"
            title={t('reloadPlayer')}
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>

          {/* Server Selector Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowServerMenu(!showServerMenu)}
              className="flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/10 text-xs md:text-sm font-medium transition-colors cursor-pointer player-btn-hover"
            >
              <Server className="w-4 h-4 text-neutral-300" />
              <span className="hidden md:inline text-neutral-300">{t('server')}:</span>
              <span className="text-white font-semibold truncate max-w-[90px] md:max-w-none">
                {serverDisplayName.split(' ')[0]}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-neutral-400" />
            </button>

            {showServerMenu && (
              <div
                className={`absolute top-full mt-2 w-64 bg-[#1e1e1e] border border-white/10 rounded-xl shadow-2xl overflow-hidden py-1 z-50 ${
                  isRtl ? 'left-0 md:right-0 md:left-auto' : 'right-0 md:left-0 md:right-auto'
                }`}
              >
                <div className="px-3 py-2 text-[11px] font-semibold text-neutral-400 uppercase tracking-wider border-b border-white/5">
                  {t('changeServer')}
                </div>
                {SERVERS.map((server) => (
                  <button
                    key={server.id}
                    onClick={() => {
                      setSelectedServerId(server.id);
                      setShowServerMenu(false);
                    }}
                    className={`w-full px-3 py-2.5 flex items-center justify-between text-xs hover:bg-white/10 transition-colors cursor-pointer ${
                      server.id === selectedServerId ? 'bg-white/15 text-white font-bold' : 'text-neutral-300'
                    }`}
                  >
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-neutral-400">
                      {isRtl ? server.tagHe : server.tag}
                    </span>
                    <span>{isRtl ? server.nameHe : server.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Controls: Ad-Shield & Close */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAdShieldEnabled(!adShieldEnabled)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition-colors cursor-pointer player-btn-hover ${
              adShieldEnabled
                ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/25'
                : 'bg-neutral-800 border-neutral-700 text-neutral-400 hover:bg-neutral-700'
            }`}
            title={
              adShieldEnabled
                ? (isRtl ? 'מגן פופ-אפים שקט מופעל (ללא זיהוי)' : 'Silent Ad-Shield Active (undetected)')
                : (isRtl ? 'מגן פופ-אפים כבוי' : 'Ad-Shield Disabled')
            }
          >
            {adShieldEnabled ? (
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            ) : (
              <ShieldAlert className="w-3.5 h-3.5 text-neutral-400" />
            )}
            <span className="hidden sm:inline">
              {adShieldEnabled
                ? (isRtl ? 'מגן פופ-אפים פעיל' : 'Ad-Shield Active')
                : (isRtl ? 'ללא מגן' : 'Shield Off')}
            </span>
          </button>

          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-neutral-300 hover:text-white transition-colors border border-white/10 cursor-pointer player-btn-hover"
            title={t('closePlayer')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* ─── Video Embed Viewport ─── */}
      <div className="relative flex-1 w-full h-full bg-black overflow-hidden flex items-center justify-center">
        {/* ─── Smart Server Fallback Pop-up Dialog ─── */}
        {showFallbackPrompt && (
          <div className="absolute inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in">
            <div className="max-w-md w-full bg-[#181818]/95 border border-amber-500/50 rounded-2xl p-6 shadow-[0_0_50px_rgba(0,0,0,0.9)] relative text-center flex flex-col items-center gap-4 animate-in zoom-in-95 duration-200">
              {/* Close / Dismiss */}
              <button
                onClick={() => {
                  setShowFallbackPrompt(false);
                  setCountdownActive(false);
                }}
                className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white transition-colors cursor-pointer"
                title={t('close')}
              >
                <X className="w-4 h-4" />
              </button>

              {/* Glowing Warning / Server Icon */}
              <div className="w-16 h-16 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 shadow-[0_0_24px_rgba(245,158,11,0.25)]">
                <AlertTriangle className="w-8 h-8 animate-bounce" />
              </div>

              {/* Title & Description */}
              <div className="space-y-2">
                <h3 className="text-lg md:text-xl font-bold text-white tracking-tight">
                  {t('movieNotFoundOnServer')}
                </h3>
                <p className="text-xs md:text-sm text-neutral-300 leading-relaxed px-2">
                  {isRtl
                    ? `נראה שהסרט או הפרק אינם זמינים בשרת "${serverDisplayName.split(' ')[0]}" או שחלה שגיאה בטעינת הזרם. האם ברצונך לעבור לשרת הבא ("${nextServerDisplayName.split(' ')[0]}")?`
                    : `It looks like this movie is unavailable on server "${serverDisplayName.split(' ')[0]}" or an error occurred. Would you like to switch to "${nextServerDisplayName.split(' ')[0]}"?`}
                </p>
              </div>

              {/* Countdown Bar & Badge */}
              {countdownActive && (
                <div className="w-full bg-white/5 border border-white/10 rounded-xl p-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-xs text-amber-300 font-medium">
                    <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
                    <span>
                      {t('autoSwitchCountdown')}{' '}
                      <strong className="text-amber-200 font-mono text-sm px-1">
                        {fallbackCountdown}
                      </strong>{' '}
                      {t('secondsAbbr')}
                    </span>
                  </div>
                  <button
                    onClick={() => setCountdownActive(false)}
                    className="text-[11px] px-2.5 py-1 rounded bg-white/10 hover:bg-white/20 text-neutral-300 transition-colors cursor-pointer"
                  >
                    {isRtl ? 'השהה ספירה' : 'Pause timer'}
                  </button>
                </div>
              )}

              {/* Action Buttons */}
              <div className="w-full flex flex-col gap-2.5 pt-2">
                <button
                  onClick={handleNextServer}
                  className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-[#E50914] to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold text-sm shadow-lg shadow-red-600/30 flex items-center justify-center gap-2 transition-all player-btn-hover cursor-pointer"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>{t('switchNextServerNow')}</span>
                </button>
              </div>

              <div className="w-full flex items-center justify-between gap-2 pt-1 text-xs">
                <button
                  onClick={() => {
                    setShowFallbackPrompt(false);
                    setCountdownActive(false);
                    setShowServerMenu(true);
                  }}
                  className="text-neutral-400 hover:text-white underline cursor-pointer py-1 transition-colors"
                >
                  {t('chooseServerManually')}
                </button>
                <button
                  onClick={() => {
                    setShowFallbackPrompt(false);
                    setCountdownActive(false);
                  }}
                  className="text-neutral-400 hover:text-white underline cursor-pointer py-1 transition-colors"
                >
                  {t('stayOnServer')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Floating Responsive Notification Banner if slow or stuck */}
        {(streamState === 'slow' || streamState === 'stuck') && !showFallbackPrompt && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-40 max-w-xl w-[92%] bg-[#181818]/95 border border-amber-500/40 rounded-xl px-4 py-2.5 shadow-2xl backdrop-blur-md flex items-center justify-between gap-3 text-white text-xs animate-in fade-in slide-in-from-top-2">
            <div className="flex items-center gap-2.5 min-w-0">
              <AlertTriangle
                className={`w-4 h-4 shrink-0 ${
                  streamState === 'stuck' ? 'text-rose-400 animate-pulse' : 'text-amber-400'
                }`}
              />
              <span className="truncate">
                {streamState === 'stuck'
                  ? (isRtl ? `השרת אינו מגיב (עברו ${elapsedSeconds} שניות).` : `Server unresponsive (${elapsedSeconds}s).`)
                  : (isRtl ? `טעינת השרת מתעכבת (${elapsedSeconds} שניות).` : `Loading is taking longer (${elapsedSeconds}s).`)}
                {' '}
                {isRtl ? 'מומלץ להחליף שרת:' : 'Recommended to switch:'}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleNextServer}
                className="px-3 py-1.5 rounded-lg bg-[#E50914] hover:bg-red-700 text-white font-semibold transition-colors cursor-pointer text-xs flex items-center gap-1"
              >
                <RefreshCw className="w-3 h-3" />
                <span>{t('nextServer')}</span>
              </button>
              <button
                onClick={() => setStreamState('ready')}
                className="p-1 rounded hover:bg-white/10 text-neutral-400 hover:text-white cursor-pointer"
                title={t('close')}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Loading Overlay with Live Percentage Counter */}
        {showLoadingOverlay && (
          <div
            className={`absolute inset-0 z-30 bg-[#0a0a0a]/92 backdrop-blur-md flex flex-col items-center justify-center p-6 text-center transition-opacity duration-500 ${
              loadingProgress === 100 ? 'opacity-0 pointer-events-none' : 'opacity-100'
            }`}
          >
            {/* Glowing Circular Ring with Percentage */}
            <div className="relative w-32 h-32 flex items-center justify-center mb-6">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  className="stroke-white/10 fill-none"
                  strokeWidth="5"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  className="stroke-[#E50914] fill-none transition-all duration-300 ease-out"
                  strokeWidth="5"
                  strokeDasharray={264}
                  strokeDashoffset={264 - (264 * loadingProgress) / 100}
                  strokeLinecap="round"
                />
              </svg>

              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl md:text-4xl font-black text-white font-mono tracking-tight drop-shadow-[0_0_12px_rgba(229,9,20,0.5)]">
                  {loadingProgress}%
                </span>
                <span className="text-[10px] uppercase font-bold text-neutral-400 tracking-wider mt-0.5">
                  {t('loading')}
                </span>
              </div>
            </div>

            {/* Status title & description */}
            <h3 className="text-base md:text-lg font-bold text-white mb-1.5 flex items-center gap-2">
              <Zap className="w-4 h-4 text-[#E50914] animate-pulse" />
              <span>{t('loadingStream')} {serverDisplayName.split(' ')[0]}</span>
            </h3>
            <p className="text-xs text-neutral-300 font-medium h-5 transition-all">
              {loadingStatus}
            </p>

            {/* Linear Progress Bar */}
            <div className="w-72 max-w-full h-2 bg-white/10 rounded-full overflow-hidden mt-4 mb-6 p-0.5 border border-white/5">
              <div
                className="h-full bg-gradient-to-r from-[#E50914] via-red-500 to-amber-500 transition-all duration-200 ease-out rounded-full shadow-[0_0_12px_rgba(229,9,20,0.8)]"
                style={{ width: `${loadingProgress}%` }}
              />
            </div>

            {/* Quick Actions if slow */}
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => setShowLoadingOverlay(false)}
                className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-semibold text-neutral-300 hover:text-white transition-colors border border-white/10 cursor-pointer flex items-center gap-1.5"
              >
                <span>{t('openPlayerDirectly')}</span>
              </button>

              <button
                onClick={handleNextServer}
                className="px-4 py-2 rounded-lg bg-[#E50914] hover:bg-red-700 text-xs font-semibold text-white transition-colors border border-red-500/40 cursor-pointer flex items-center gap-1.5"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>{t('nextServer')}</span>
              </button>
            </div>
          </div>
        )}

        {/* Video Embed Iframe with Silent Ad-Shield Sandbox */}
        <iframe
          key={`${selectedServerId}-${currentSeason}-${currentEpisode}-${activeTmdbId || tmdbId || ''}-${reloadKey}-${adShieldEnabled ? 'shielded' : 'open'}`}
          src={streamUrl}
          title={title}
          onLoad={handleIframeLoad}
          className="w-full h-full border-0"
          sandbox={
            adShieldEnabled
              ? 'allow-scripts allow-same-origin allow-forms allow-presentation'
              : undefined
          }
          allow="autoplay; fullscreen; encrypted-media; picture-in-picture; payment"
          allowFullScreen
          referrerPolicy="origin"
        />
      </div>
    </div>
  );
}
